// Portions of this code are adapted from the MUI Toolbar component.
// Source: https://github.com/mui/toolpad/blob/master/packages/toolpad-core/src/DashboardLayout/DashboardLayout.tsx
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE
import { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

import { Appbar, AppbarProps } from './Appbar';
import { Sidebar, SidebarProvider } from './Sidebar';
import { NavigationItem } from './types';

interface DashboardLayoutProps {
  navItems?: NavigationItem[];

  /**
   * Whether the sidebar should be disabled.
   * @default false
   */
  disableSidebar?: boolean;

  /**
   * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
   * @default false
   */
  disableCollapsibleSidebar?: boolean;

  /**
   * Width of the sidebar when expanded.
   * @default 320
   */
  sidebarExpandedWidth?: number | string;

  /**
   * Appbar component slots.
   * @optional
   */
  appbarSlots?: AppbarProps['slots'];

  /**
   * Appbar component slot props.
   * @optional
   */
  appbarSlotProps?: AppbarProps['slotProps'];

  /**
   * The content of the dashboard.
   */
  children: ReactNode;
}

export function DashboardLayout({
  navItems = [],
  disableSidebar = false,
  disableCollapsibleSidebar = false,
  sidebarExpandedWidth = 320,
  appbarSlots,
  appbarSlotProps,
  children,
}: DashboardLayoutProps) {
  return (
    <SidebarProvider>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          overflow: 'hidden',
          height: '100vh',
          width: '100vw',
        }}
      >
        <Appbar
          disableCollapsibleSidebar={disableCollapsibleSidebar}
          hideNavigation={disableSidebar}
          slots={appbarSlots}
          slotProps={appbarSlotProps}
        />

        {!disableSidebar && (
          <Sidebar
            navigation={navItems}
            disableCollapsibleSidebar={disableCollapsibleSidebar}
            sidebarExpandedWidth={sidebarExpandedWidth}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Toolbar sx={{ displayPrint: 'none' }} />
          <Box
            component="main"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </SidebarProvider>
  );
}

export default DashboardLayout;
